/**
 * PublicThankYou — post-payment confirmation page.
 *
 * Route: /p/:handle/:slug/thanks?session_id=…
 * Buyers see this immediately after payment redirects back. We offer
 * a one-click "Send me access link" CTA that fires the magic-link email
 * so they can land directly in their customer portal without setting a
 * password.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Loader2, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

export default function PublicThankYou() {
  const { handle, slug } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_BASE}/public/page/${handle}/${slug}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ workspace: { name: handle } }); });
    return () => { alive = false; };
  }, [handle, slug]);

  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!email) { toast.error("Enter the email you used at checkout"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/magic-link/request`, {
        email,
        redirect_path: "/customer",
      });
      setSent(true);
      toast.success("Sign-in link sent — check your inbox");
    } catch {
      toast.error("Could not send link");
    }
    setBusy(false);
  };

  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-[#FAFAFA]"><Loader2 className="h-6 w-6 animate-spin text-ai" /></div>;
  }

  const { workspace, asset } = data;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-thanks">
      <header className="border-b border-line bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <p className="font-display text-lg font-bold text-ink">{workspace?.name || handle}</p>
          <p className="text-xs text-meta">Powered by Ashra AI</p>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-emerald-100 grid place-items-center mx-auto">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="mt-6 font-display text-4xl font-extrabold text-ink leading-tight">Payment received — you're in!</h1>
        {asset && (
          <p className="mt-3 text-lg text-meta">
            Your purchase of <span className="font-semibold text-ink">{asset.title}</span> is confirmed.
          </p>
        )}

        {/* Instant access via magic link */}
        <div className="mt-8 surface p-6 text-left" data-testid="public-thanks-instant-access">
          <p className="font-display text-lg font-bold text-ink inline-flex items-center gap-2"><Mail className="h-4 w-4 text-ai" /> Get instant access</p>
          <p className="text-sm text-meta mt-1">Enter the email you used at checkout — we'll send a one-click link straight into your customer portal.</p>
          {sent ? (
            <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700 inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Link sent to <span className="font-semibold">{email}</span>. Check your inbox.
            </div>
          ) : (
            <form onSubmit={sendMagicLink} className="mt-3 flex gap-2">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="public-thanks-email-input"
                className="flex-1 rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <button
                type="submit" disabled={busy}
                data-testid="public-thanks-send-link"
                className="rounded-full ashra-gradient-bg px-4 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />} Send
              </button>
            </form>
          )}
          {sessionId && (
            <p className="mt-3 text-[11px] font-mono text-meta-soft">Order ref: {sessionId}</p>
          )}
        </div>
      </main>

      <footer className="border-t border-line py-8 text-center text-xs text-meta">
        © {new Date().getFullYear()} {workspace?.name || handle} · Built on Ashra AI
      </footer>
    </div>
  );
}
